import {captureException} from '@sentry/browser';
import React from 'react';

import {CidIdMap} from 'notebook/notes/NoteConstants';
import notifications, {Response as NotificationResponse} from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  FLOWCHART_CONFIG_SAVE_STARTED_EVENT,
  FLOWCHART_CONFIG_SAVE_ENDED_EVENT,
  composeFlowchartEventMetadata,
} from 'performance/processProcessesEvents';
import {PersistenceInfo} from 'procedures/ProcedurePageContext';
import {
  FlowchartConfigWithMetadataAndRootTaskSchema,
  ProcessFlowchartAdapter,
  ProcessFlowchartAdditionalPersistedData,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {getErrorMessageForLogging} from 'util/RequestCorrelation';

import {ProcessFlowchartConfigForEditorWithLatestVersionGraphQL} from '../../../../../../graphql-fragments/processes/__generated/ProcessFlowchartConfigForEditorWithLatestVersionGraphQL';
import {countEdgesByType, ProcessFlowchartNodeType} from '../processFlowchartConfigEditorConstants';
import {isStaticTaskSchemaNodeData, StaticFlowchartNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {getFlowchartConfigSafely} from '../processFlowchartConfigEditorUtils';
import {
  FlowchartConfigWithMetadata,
  setIsFlowchartConfigAutosaverBlocked,
  setSaveInFlightWithError,
  setAutosaverPausedForDisplayableSaveError,
} from './flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigMetadata} from './reduceFlowchartConfig';

/**
 * Handles errors that occur during the post-save updater.
 * Reports the error to Sentry but returns the fallback config to ensure the version ID is still updated.
 * This prevents 409 conflicts on subsequent saves even if onSaveComplete fails.
 */
function handleUpdaterError(
  error: unknown,
  flowchartConfigId: string,
  fallbackConfig: FlowchartConfigWithMetadataAndRootTaskSchema
): FlowchartConfigWithMetadataAndRootTaskSchema {
  const errorMessage = error instanceof Error ? error.message : String(error);
  captureException(new Error(`Flowchart config updater failed: ${errorMessage}`), {
    level: 'error',
    tags: {
      flowchart_config_id: flowchartConfigId,
    },
    extra: {
      originalError: error,
    },
  });

  // Return the fallback config which has the updated version ID.
  // This ensures subsequent saves won't get 409 conflicts.
  return fallbackConfig;
}

interface HttpSaveError extends NotificationResponse {
  statusText?: string;
  responseJSON?: {errors?: Record<string, string>};
}

/**
 * Displays a save error notification to the user.
 * If field-level errors are present, shows them along with the friendly message.
 * Otherwise, shows a generic error notification.
 */
function displaySaveErrorNotification(error: HttpSaveError): void {
  const fieldErrorMessages = Object.values(error.responseJSON?.errors ?? {});
  if (fieldErrorMessages.length > 0) {
    const friendlyMessage =
      notifications.getFriendlyErrorAsElement(error, '') ?? 'Your request could not be completed.';
    notifications.error(
      <>
        {friendlyMessage}
        <br />
        {fieldErrorMessages.map((msg, idx) => (
          <React.Fragment key={idx}>
            {msg}
            {idx < fieldErrorMessages.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>,
      {duration: 0}
    );
  } else {
    notifications.notifyError(error, {duration: 0});
  }
}

/**
 * Reports a save error to Sentry for monitoring.
 */
function logSaveErrorToSentry(error: HttpSaveError, flowchartConfigId: string): void {
  const errorMessage = getErrorMessageForLogging(error);
  captureException(new Error(`Flowchart config save failed with ${error.status}: ${errorMessage}`), {
    level: 'error',
    tags: {
      error_status: error.status,
      flowchart_config_id: flowchartConfigId,
    },
    extra: {
      responseJSON: error.responseJSON,
      statusText: error.statusText,
    },
  });
}

/**
 * Handles HTTP errors that occur during flowchart config save.
 * Displays appropriate notifications, pauses autosaver for recoverable errors, and logs to Sentry.
 */
function handleHttpSaveError(error: HttpSaveError, flowchartConfigId: string): void {
  displaySaveErrorNotification(error);

  // For 400 errors, pause the autosaver until the user makes a new change.
  // This prevents repeatedly autosaving the same invalid state.
  if (error.status === 400) {
    setAutosaverPausedForDisplayableSaveError(flowchartConfigId, true);
  }

  // A status code of zero is a client-side network error, we don't need to track that
  if (error.status !== 0) {
    logSaveErrorToSentry(error, flowchartConfigId);
  }
}

/**
 * Updates the currentVersionId for task schema nodes using the cidToIdMap returned from the server.
 * This is necessary because:
 * 1. When a new node is created, its currentVersionId is null - after saving, the server generates an ID
 * 2. When an existing node is updated, the server may create a new version with a new ID
 * In both cases, we need to update currentVersionId to the latest version ID from the server.
 * @visibleForTesting
 */
export function updateTaskDefinitionVersionIds(
  nodes: Array<StaticFlowchartNodeData>,
  cidToIdMap: CidIdMap
): Array<StaticFlowchartNodeData> {
  return nodes.map((node) => {
    if (!isStaticTaskSchemaNodeData(node)) {
      return node;
    }

    const clientId = node.taskDefinitionVersionSpec.clientId;
    const currentVersionId = node.taskDefinitionVersionSpec.currentVersionId;
    const newVersionId = cidToIdMap[clientId];

    // Update if we have a new ID from the server that differs from the current version ID
    if (newVersionId != null && String(newVersionId) !== currentVersionId) {
      return {
        ...node,
        taskDefinitionVersionSpec: {
          ...node.taskDefinitionVersionSpec,
          currentVersionId: String(newVersionId),
        },
      };
    }

    return node;
  });
}

type SaveHandler = (
  flowchartConfig: FlowchartConfigWithMetadata,
  isBlocking?: boolean,
  persistenceInfo?: PersistenceInfo
) => Promise<null | FlowchartConfigMetadata>;

type SetPendingDraftFlowchartUpdate = null | {
  updater: (
    latestDraftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata
  ) => Promise<FlowchartConfigWithMetadata>;
};

/**
 * This factory for making a saveHandler is used to ensure that the saveHandler is protected from
 * unmounted components.
 *
 * Callers must ensure that the callbacks are noops if the component is unmounted. They are only provided
 * to allow components to do bookkeeping, which isn't needed once the component is unmounted.
 *
 * The factory returns a saveHandler that can be passed to the AutosaveManager.
 */
export function saveHandlerFactory(
  handleSave: (
    snapShotFlowchartConfigWithMetadata: FlowchartConfigWithMetadata,
    context?: PersistenceInfo
  ) => Promise<null | {
    flowchartConfigGQL: ProcessFlowchartConfigForEditorWithLatestVersionGraphQL | null;
    snapshotFlowchartConfigWithMetadata: FlowchartConfigWithMetadataAndRootTaskSchema;
    additionalPersistedData: ProcessFlowchartAdditionalPersistedData;
  }>,
  setPendingDraftFlowchartUpdate: (newUpdate: SetPendingDraftFlowchartUpdate) => Promise<void>,
  processFlowchartConfigId: string
): SaveHandler {
  return (flowchartConfig: FlowchartConfigWithMetadata, isBlocking?: boolean) =>
    saveHandler(flowchartConfig, isBlocking, {
      handleSave,
      setPendingDraftFlowchartUpdate,
      processFlowchartConfigId,
    });
}

interface FactoryOptions {
  handleSave: (
    snapShotFlowchartConfigWithMetadata: FlowchartConfigWithMetadata,
    context?: PersistenceInfo
  ) => Promise<null | {
    flowchartConfigGQL: ProcessFlowchartConfigForEditorWithLatestVersionGraphQL | null;
    snapshotFlowchartConfigWithMetadata: FlowchartConfigWithMetadataAndRootTaskSchema;
    additionalPersistedData: ProcessFlowchartAdditionalPersistedData;
  }>;
  setPendingDraftFlowchartUpdate: (newUpdate: SetPendingDraftFlowchartUpdate) => Promise<void>;
  processFlowchartConfigId: string;
}

async function saveHandler(
  // --- Provided by the caller (i.e., the AutosaveManager)
  // This is coming from FlowchartConfigAutosaveManagerCallbacks.getDataToSave()
  // which pulls from redux store at the start of the save
  snapShotFlowchartConfigWithMetadata: FlowchartConfigWithMetadata,
  isBlocking: boolean = false,
  // --- Provided by the factory
  factoryOptions: FactoryOptions
): Promise<FlowchartConfigMetadata | null> {
  const {handleSave, setPendingDraftFlowchartUpdate, processFlowchartConfigId} = factoryOptions;

  const typedFlowchartConfig = getFlowchartConfigSafely(snapShotFlowchartConfigWithMetadata);

  const saveEventMetadata = composeFlowchartEventMetadata({
    flowchartType: typedFlowchartConfig.metadata.flowchartTypeName,
    numNodes: typedFlowchartConfig.staticFlowchart.nodesStaticData.length,
    ...countEdgesByType(typedFlowchartConfig.staticFlowchart.edgesStaticData),
    isBlocking,
    flowchartConfigId: processFlowchartConfigId,
  });
  logProfilerTimingEvent(FLOWCHART_CONFIG_SAVE_STARTED_EVENT, saveEventMetadata);

  const flowchartAdapter: ProcessFlowchartAdapter<unknown, unknown> = PLUGIN_REGISTRY_INSTANCE.getNodeAdapter(
    ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    typedFlowchartConfig.metadata.rootTaskSchema.executionType
  ) as ProcessFlowchartAdapter<unknown, unknown>;
  // Allow flowchart adapter to perform any pre-save work
  flowchartAdapter.onSaveStart(typedFlowchartConfig);

  try {
    // This is calling the DAO functions, etc
    const result = await handleSave(snapShotFlowchartConfigWithMetadata);

    if (result != null) {
      await setPendingDraftFlowchartUpdate({
        updater: async (latestDraftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata) => {
          // latestDraftFlowchartConfigAndMetadata contains any changes made in flight during this save

          const typedLatestDraftFlowchartConfig = getFlowchartConfigSafely(
            latestDraftFlowchartConfigAndMetadata
          );

          // Update task definition version IDs for nodes that were newly created or updated
          const nodesWithUpdatedVersionIds = updateTaskDefinitionVersionIds(
            typedLatestDraftFlowchartConfig.staticFlowchart.nodesStaticData,
            result.additionalPersistedData.cidToIdMap
          );

          // Update the currentVersionId in the draft flowchart config metadata
          const draftFlowchartConfigWithUpdates: FlowchartConfigWithMetadataAndRootTaskSchema = {
            ...typedLatestDraftFlowchartConfig,
            staticFlowchart: {
              ...typedLatestDraftFlowchartConfig.staticFlowchart,
              nodesStaticData: nodesWithUpdatedVersionIds,
            },
            metadata: {
              ...typedLatestDraftFlowchartConfig.metadata,
              currentVersionId: result.additionalPersistedData.currentVersionId,
            },
          };

          // It's possible that this updater was called even though no server call was made (e.g.,
          // see handleSave in ProcessFlowchartConfigEditorController), or that no flowchartConfigGQL is
          // returned (see saveFlowchartConfigWhenUnmountedFactory)
          if (result.flowchartConfigGQL == null) {
            return draftFlowchartConfigWithUpdates;
          }

          // Give the adapter a chance to do additional processing after save.
          // Wrap in try/catch to ensure version ID is updated even if onSaveComplete fails.
          try {
            const valuableServerData =
              flowchartAdapter.extractValuableDataFromServerResponse?.(result.flowchartConfigGQL) ?? null;

            const draftFlowchartConfigWithSaveCompleteUpdates = await flowchartAdapter.onSaveComplete(
              draftFlowchartConfigWithUpdates,
              {...result.additionalPersistedData, valuableServerData}
            );

            return draftFlowchartConfigWithSaveCompleteUpdates ?? draftFlowchartConfigWithUpdates;
          } catch (updaterError) {
            // Intentionally not re-throwing: we want to return the fallback config
            // which has the updated version ID to prevent 409 conflicts on subsequent saves.
            // The error is reported to Sentry by handleUpdaterError.
            const shouldRethrow = false;
            if (shouldRethrow) {
              throw updaterError;
            }
            return handleUpdaterError(
              updaterError,
              processFlowchartConfigId,
              draftFlowchartConfigWithUpdates
            );
          }
        },
      });
    }
    return result?.snapshotFlowchartConfigWithMetadata.metadata ?? null;
  } catch (e) {
    if (e.status === 409) {
      // The update conflicts with the version stored on the server.
      setIsFlowchartConfigAutosaverBlocked(processFlowchartConfigId, true);
      const userMessage = notifications.getFriendlyError(e);
      if (userMessage != null) {
        notifications.error(userMessage, {duration: 0});
      }
      return null;
    } else {
      // Directly dispatch the error state (reducer will automatically set isDirty=true)
      setSaveInFlightWithError(processFlowchartConfigId, false, e);

      if (e.status != null) {
        // 5xx errors lock the flowchart so the user must refresh to retry.
        if (e.status >= 500) {
          setIsFlowchartConfigAutosaverBlocked(processFlowchartConfigId, true);
        }
        handleHttpSaveError(e, processFlowchartConfigId);
        return null;
      }

      // This is a non-HTTP error. That's unexpected. Throw.
      throw e;
    }
  } finally {
    logProfilerTimingEvent(FLOWCHART_CONFIG_SAVE_ENDED_EVENT, saveEventMetadata);
  }
}
